import React from 'react';

import { ENV } from '@environment';
import GoogleMapReact from 'google-map-react';

import AppBar from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import Carousel from 'components/Carousel';
import HotelStars from 'components/HotelsStars';
import Loader from 'components/Loader';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import SimpleAlert from 'components/SimpleAlert';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useUserAccount from 'hooks/useUserAccount';
import { ConfirmMessage } from 'models/confirmMessage';
import { HotelDetailsCompleteResponse, RoomRatePrice } from 'models/hotelDetailsComplete';
import SolicitationSuccess from 'screens/components/SolicitationSuccess';
import { HotelAmenities } from 'services/hotelService';
import { TravelArrangerUsersAllowed } from 'services/userService';
import { htmlT } from 'utils/generic';
import Tooltip from 'components/Tooltips/Tooltip';

import ConfirmApproveModal from '../Dashboard/components/ConfirmApproveModal';
import AboutHotel from './components/AboutHotel';
import Amenities from './components/Amenities';
import BestRoom from './components/BestRoom';
import BookingInfo from './components/BookingInfo';
import MapPin from './components/MapPin';
import RoomList from './components/RoomList';
import {
  Container,
  SubTitle,
  Info,
  Detail,
  MapBox,
  MapBottomAddress,
  Separator,
  RoomsHeader,
  AlertContainer,
  InfoFullAddress,
  CardFooterScore,
  CardFooterScoreValue,
  CardFooterScoreText,
} from './styles';
import { RootState } from 'store';
import { useSelector } from 'react-redux';

interface Props {
  show: boolean;
  loading: boolean;
  creating: boolean;
  details?: HotelDetailsCompleteResponse;
  onClose: () => void;
  onConfirm: (
    justifyForBreakingRules: string,
    reason: string,
    roomRateId: string,
    roomRate: { refundableType: string },
    roomRatePrice: RoomRatePrice,
    costCenterId?: string,
    breakRulesReasonId?: string,
    card?: string
  ) => void;
  amenitiesCodes: HotelAmenities[];
  showSuccess: boolean;
  travelers?: TravelArrangerUsersAllowed[];
  showConfirmFlow: boolean;
  confirmFlowMessage: ConfirmMessage;
  onConfirmFlow: () => void;
  onConfirmFlowClose: () => void;
  setShowConfirmProfile: (value: boolean) => void;
  showConfirmProfile: boolean;
  setShowTravelJustify: (value: boolean) => void;
  showTravelJustify: boolean;
}

const HotelDetails = ({
  show,
  loading,
  creating,
  details,
  onClose,
  onConfirm,
  amenitiesCodes,
  showSuccess,
  travelers,
  onConfirmFlow,
  showConfirmFlow,
  confirmFlowMessage,
  onConfirmFlowClose,
  setShowConfirmProfile,
  showConfirmProfile,
  setShowTravelJustify,
  showTravelJustify,
}: Props) => {
  const { t } = useStoreTranslation();
  const userAccount = useUserAccount();
  const isMobile = useIsMobile();
  const travel_itinerary_id = useSelector((state: RootState) => state.travel.travelItineraryId);
  
  if (details !== null && details !== undefined) {
    details.address = details.address.replace('()', '');
  }

  const mapCenter = {
    lat: details?.latitude as number,
    lng: details?.longitude as number,
  };

  const generateFullAddress = () => {
    let address = details?.address;
    if (details?.cityName && address.sanitize() !== details.cityName.sanitize()) address += `, ${details.cityName}`;

    if (details?.stateName) address += `, ${details.stateName}`;

    if (details?.countryName) address += `, ${details.countryName}`;

    return address;
  };

  const headerSubtitleTooltip = () => {
    if (travelers.length > 1) {
      return (
    <Tooltip
          whiteColor={true}
          event="hover"
          content={
            getTooltipContent()
          }
        >
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <span>
            {htmlT(t, 'flightsSearch.toTraveler', {
              user: getTravelersNames(),
            })}
          </span> &nbsp; 
          <img width={20} height={20} src="/corporate/images/advice-2.svg" alt="" />
        </div>
      </Tooltip>
      );
    } else {
      return (
        <span style={{ display: 'inline'}}>
          {htmlT(t, 'flightsSearch.toTraveler', { user: getTravelersNames()})}
        </span>
      );
    }    
  };

  const getTooltipContent = () => (
    <>
      <div style={{ marginBottom: 18, fontWeight: 600 }}>
        {t('flightsSearch.additionalTraveler')}
      </div>
      {travelers.slice(1).map((t, index) => (
        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <img src="/corporate/images/check-copy.svg" alt="" width={14} height={14} />
          <span>
            {t.name} {t.lastName}
          </span>
        </div>
      ))}
    </>
  );

  const getTravelersNames = () => (
    `<span style='color: #F4BF31; font-weight: 600; font-size: 16px; padding-left: 4px; display: contents'>
      ${travelers[0]?.name} ${travelers[0]?.lastName} ${
        travelers.length > 1 ? '+ ' + (travelers.length - 1) : ''
      }
    </span>`
  );

  const fullAddress = generateFullAddress();

  const googleMapsKey = ENV.API_KEYS.GOOGLE_MAPS_API_KEY as string;

  const renderContent = () => {
    if (details) {
      return (
        <>
          {details.images && <Carousel height={200} fit="cover" images={details.images} />}
          <Detail>
            <HotelStars count={5} value={details.category} />
            <SubTitle>{details.hotelName}</SubTitle>            
            <Info>{fullAddress}</Info>
            <InfoFullAddress>{details.fullAddress}</InfoFullAddress>
            {details.score > 0 && 
              (<CardFooterScore>
                <CardFooterScoreValue>
                  {details.score}
                </CardFooterScoreValue>
                <CardFooterScoreText>
                  {details.score >= 9 ? t('hotelsCard.excellent') : null }
                  {details.score >= 8 && details.score < 9 ? t('hotelsCard.veryGood') : null }
                  {details.score >= 7 && details.score < 8 ? t('hotelsCard.good') : null }
                  {details.score >= 6 && details.score < 7 ? t('hotelsCard.acceptable') : null }
                  {details.score < 6 ? t('hotelsCard.regular') : null }
                </CardFooterScoreText>            
              </CardFooterScore>)
            }
          </Detail>
          <Detail>
            <BookingInfo details={details} />
          </Detail>
          <Detail>
            <BestRoom
              rooms={details.rooms}
              checkinDate={details.checkinDate}
              checkoutDate={details.checkoutDate}
              currencyCode={details.currencyCode}
              alternativeCurrencyCode={details?.rooms?.first()?.roomRate?.alternativePrice?.currencyCode ?? ''}
            />
          </Detail>
          <Detail>
            <AboutHotel details={details} />
          </Detail>
          <Detail>
            <Amenities amenities={details.ammenities} amenitiesCodes={amenitiesCodes} />
          </Detail>

          <MapBox>
            <GoogleMapReact
              bootstrapURLKeys={{ key: googleMapsKey }}
              defaultCenter={mapCenter}
              defaultZoom={10}
              options={{ fullscreenControl: false, gestureHandling: 'cooperative' }}
            >
              <MapPin lat={details.latitude} lng={details.longitude} />
            </GoogleMapReact>
            <MapBottomAddress>{details.address}</MapBottomAddress>
          </MapBox>
          <RoomsHeader id="hotel-details-rooms">{t('hotelDetails.selectRoom')}</RoomsHeader>
          <Separator />
          <RoomList
            creating={creating}
            details={details}
            onConfirm={onConfirm}
            bookingDisabled={!userAccount?.bookingAllowed}
            showTravelJustify={showTravelJustify}
            showConfirmProfile={showConfirmProfile}
            setShowConfirmProfile={setShowConfirmProfile}
            setShowTravelJustify={setShowTravelJustify}
          />
          <AlertContainer>
            {!userAccount?.bookingAllowed && (
              <SimpleAlert title={t('bookingNotAllowedAlert.title')} content={t('bookingNotAllowedAlert.content')} />
            )}
          </AlertContainer>
          <SolicitationSuccess show={showSuccess} isHotel={true} isFlight={false} testId="hotel-solicitation-success" tid={travel_itinerary_id} />
        </>
      );
    }
    return null;
  };

  return (
    <>
      <ApplicationOverlayContent show={show} width={800} onClose={onClose} testId="hotel-details">
        <Container>
          <Mobile>
            <AppBar goBack={onClose}>{t('hotelDetails.title')}</AppBar>
          </Mobile>

          <Desktop>
            <ModalHeader
              onClose={onClose}
              title={t('hotelDetails.title')}
              subtitle={
                travelers && travelers.length > 0 ? (
                  <>{headerSubtitleTooltip()}</>
                ) : undefined
              }
            />
          </Desktop>

          {loading ? <Loader fullScreen={isMobile} /> : renderContent()}
        </Container>
      </ApplicationOverlayContent>

      <ConfirmApproveModal
        onConfirmApprove={onConfirmFlow}
        onConfirmClose={onConfirmFlowClose}
        showConfirm={showConfirmFlow}
        confirmMessage={confirmFlowMessage}
        showCancelButton={false}
      ></ConfirmApproveModal>
    </>
  );
};

export default HotelDetails;
